/**
 * GoodLot announcement rotator.
 * Supports autoplay, touch/mouse swiping, keyboard navigation, and dismissal.
 */
export class AnnouncementBar extends HTMLElement {
  #current = 0;
  #interval;
  #closeButton;
  #pointerId;
  #pointerStartX = 0;
  #pointerStartY = 0;

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.announcement-bar__slide'));
    this.#closeButton = this.closest('.announcement-bar')?.querySelector('[data-goodlot-announcement-close]') ?? null;

    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerCancel);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#closeButton?.addEventListener('click', this.#handleClose);

    this.show(0);
    this.play();
  }

  disconnectedCallback() {
    this.suspend();
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerCancel);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#closeButton?.removeEventListener('click', this.#handleClose);
  }

  get autoplayInterval() {
    const seconds = Number.parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isFinite(seconds) ? seconds * 1000 : 0;
  }

  show(index) {
    if (!this.slides?.length) return;

    this.#current = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', String(slideIndex !== this.#current));
    });
  }

  next = () => {
    this.show(this.#current + 1);
  };

  previous = () => {
    this.show(this.#current - 1);
  };

  play = () => {
    this.suspend();
    if (this.slides?.length < 2 || !this.autoplayInterval || document.hidden) return;

    this.#interval = window.setInterval(this.next, this.autoplayInterval);
  };

  suspend = () => {
    if (this.#interval) window.clearInterval(this.#interval);
    this.#interval = undefined;
  };

  resume = () => {
    this.play();
  };

  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.#pointerId = event.pointerId;
    this.#pointerStartX = event.clientX;
    this.#pointerStartY = event.clientY;
    this.setPointerCapture?.(event.pointerId);
    this.toggleAttribute('dragging', true);
    this.suspend();
  };

  #handlePointerUp = (event) => {
    if (event.pointerId !== this.#pointerId) return;

    const deltaX = event.clientX - this.#pointerStartX;
    const deltaY = event.clientY - this.#pointerStartY;
    const isHorizontalSwipe = Math.abs(deltaX) >= 32 && Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe) {
      deltaX < 0 ? this.next() : this.previous();
    }

    this.#resetPointer();
    this.play();
  };

  #handlePointerCancel = () => {
    this.#resetPointer();
    this.play();
  };

  #resetPointer() {
    if (this.#pointerId !== undefined && this.hasPointerCapture?.(this.#pointerId)) {
      this.releasePointerCapture(this.#pointerId);
    }
    this.#pointerId = undefined;
    this.toggleAttribute('dragging', false);
  }

  #handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
      this.play();
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
      this.play();
    }
  };

  #handleVisibilityChange = () => {
    document.hidden ? this.suspend() : this.play();
  };

  #handleClose = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const announcement = this.closest('.announcement-bar');
    if (!announcement) return;

    this.suspend();
    announcement.hidden = true;
    announcement.style.display = 'none';
    window.dispatchEvent(new Event('resize'));
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
